/**
 * Note Templates Configuration
 * 
 * Contains system default templates and the prompt generator for clinical note generation.
 * Templates define the structure of clinical notes (SOAP, HPI, Progress, etc.)
 *
 * The library is the union of the built-in system templates below and the specialty
 * templates ported from HealthDesk Experimental (config/defaultSpecialtyTemplates.js),
 * converted here into this file's { id, name, sections:[{ key, title, placeholder, required }] }
 * shape. All templates remain static/system-provided (no user authoring / Firestore).
 */

import DEFAULT_SPECIALTY_TEMPLATES from "config/defaultSpecialtyTemplates";

/**
 * System default note templates
 * These are always available and cannot be modified by users.
 */
export const SYSTEM_DEFAULT_TEMPLATES = [
  {
    id: "system_soap",
    name: "SOAP Note",
    description: "Standard clinical documentation format",
    sections: [
      {
        key: "subjective",
        title: "Subjective",
        placeholder: "Chief complaint, HPI, ROS, PMH/PSH/FH/SH...",
        required: true,
      },
      {
        key: "objective",
        title: "Objective",
        placeholder: "Vital signs, physical examination findings, test results...",
        required: true,
      },
      {
        key: "assessment",
        title: "Assessment",
        placeholder: "Clinical summary, differential diagnosis, working diagnosis...",
        required: true,
      },
      {
        key: "plan",
        title: "Plan",
        placeholder: "Treatment plan, medications, follow-up, patient education...",
        required: true,
      },
    ],
  },
  {
    id: "system_hpi",
    name: "HPI Note",
    description: "History of Present Illness focused",
    sections: [
      {
        key: "chiefComplaint",
        title: "Chief Complaint",
        placeholder: "Primary reason for visit...",
        required: true,
      },
      {
        key: "hpi",
        title: "History of Present Illness",
        placeholder: "Onset, location, duration, character, aggravating/relieving factors...",
        required: true,
      },
      {
        key: "ros",
        title: "Review of Systems",
        placeholder: "Constitutional, cardiovascular, respiratory, GI, GU...",
        required: false,
      },
      {
        key: "assessment",
        title: "Assessment",
        placeholder: "Clinical impression based on the history...",
        required: true,
      },
    ],
  },
  {
    id: "system_progress",
    name: "Progress Note",
    description: "Follow-up visit documentation",
    sections: [
      {
        key: "intervalHistory",
        title: "Interval History",
        placeholder: "Changes since last visit, response to treatment...",
        required: true,
      },
      {
        key: "currentStatus",
        title: "Current Status",
        placeholder: "Current symptoms, functional status, medication adherence...",
        required: true,
      },
      {
        key: "assessment",
        title: "Assessment",
        placeholder: "Progress toward goals, updated diagnosis...",
        required: true,
      },
      {
        key: "plan",
        title: "Plan",
        placeholder: "Adjustments to treatment, next steps...",
        required: true,
      },
    ],
  },
  {
    id: "system_emergency_department",
    name: "Emergency Department Note",
    description: "Comprehensive ED documentation with MDM",
    sections: [
      {
        key: "chiefComplaint",
        title: "Chief Complaint",
        placeholder: "Primary reason for visit",
        required: true,
      },
      {
        key: "hpi",
        title: "History of Present Illness",
        placeholder: "Detailed history using OLDCARTS format...",
        required: true,
      },
      {
        key: "ros",
        title: "Review of Systems",
        placeholder: "System-by-system review...",
        required: true,
      },
      {
        key: "pastHistory",
        title: "Past Medical/Surgical/Social/Family History",
        placeholder: "PMH, PSH, Medications, Allergies, Social, Family History...",
        required: true,
      },
      {
        key: "physicalExam",
        title: "Physical Examination",
        placeholder: "General appearance, vital signs, system-by-system exam...",
        required: true,
      },
      {
        key: "diagnosticResults",
        title: "Diagnostic Results",
        placeholder: "Laboratory results, imaging findings, procedures...",
        required: false,
      },
      {
        key: "mdm",
        title: "Medical Decision Making",
        placeholder: "Problems addressed, differential diagnosis, risk assessment...",
        required: true,
      },
      {
        key: "diagnosisImpression",
        title: "Diagnosis/Impression",
        placeholder: "Primary and secondary diagnoses...",
        required: true,
      },
      {
        key: "treatmentPlan",
        title: "Treatment/Plan",
        placeholder: "Interventions, medications, procedures...",
        required: true,
      },
      {
        key: "disposition",
        title: "Disposition",
        placeholder: "Discharge, admit, transfer, follow-up...",
        required: true,
      },
      {
        key: "dischargeInstructions",
        title: "Patient Education/Discharge Instructions",
        placeholder: "Return precautions, medications, activity, follow-up...",
        required: false,
      },
      {
        key: "attestation",
        title: "Attestation",
        placeholder: "Provider attestation statement...",
        required: true,
      },
    ],
  },
];

/**
 * Convert a human-readable section label (e.g. "Obstetric History (G/P - TPAL)")
 * into a stable camelCase key ("obstetricHistory"). Parentheticals are dropped so
 * keys stay short and consistent.
 */
const toSectionKey = (title) => {
  const words = String(title || "")
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "section";
  return words
    .map((w, i) =>
      i === 0
        ? w.toLowerCase()
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join("");
};

/**
 * Convert one specialty definition from defaultSpecialtyTemplates into this file's
 * template shape. Required sections come first (required: true), then optional
 * sections (required: false). Per-section format rules / terminology become the
 * section placeholder; specialty prompt guidance is preserved at the template level.
 */
const specialtyToTemplate = (key, def) => {
  const usedKeys = new Set();
  const makeSections = (labels, required) =>
    (labels || []).map((label) => {
      let sectionKey = toSectionKey(label);
      while (usedKeys.has(sectionKey)) sectionKey = `${sectionKey}_`;
      usedKeys.add(sectionKey);
      const placeholder = def.formatRules?.[label] || def.terminology?.[label] || undefined;
      return { key: sectionKey, title: label, placeholder, required };
    });

  return {
    id: `specialty_${toSectionKey(key)}`,
    name: def.displayName || key,
    description: def.description || "",
    category: def.category || "specialty",
    parent: def.parent,
    guidance: def.promptInstructions || undefined,
    sections: [
      ...makeSections(def.requiredSections, true),
      ...makeSections(def.optionalSections, false),
    ],
  };
};

/**
 * Specialty templates ported from HealthDesk Experimental, converted to this file's shape.
 */
export const SPECIALTY_TEMPLATES = Object.entries(DEFAULT_SPECIALTY_TEMPLATES || {}).map(
  ([key, def]) => specialtyToTemplate(key, def),
);

/**
 * Full note-template library: built-in system templates + ported specialty templates.
 * System templates are tagged category "system"; specialty templates carry
 * "common" / "subspecialty" from their source data.
 */
export const ALL_NOTE_TEMPLATES = [
  ...SYSTEM_DEFAULT_TEMPLATES.map((t) => ({ ...t, category: t.category || "system" })),
  ...SPECIALTY_TEMPLATES,
];

/**
 * Get the full selectable note-template library.
 */
export const getNoteTemplates = () => ALL_NOTE_TEMPLATES;

/**
 * Look up a template by id, falling back to the default template when not found.
 */
export const getTemplateById = (id) =>
  ALL_NOTE_TEMPLATES.find((t) => t.id === id) || getDefaultTemplate();

/**
 * Get the default template (Emergency Department)
 */
export const getDefaultTemplate = () => {
  return SYSTEM_DEFAULT_TEMPLATES.find(t => t.id === "system_emergency_department") || SYSTEM_DEFAULT_TEMPLATES[0];
};

/**
 * Generate LLM system prompt from template sections
 * 
 * @param {string} templateName - Name of the note template
 * @param {Array} sections - Array of section definitions
 * @param {Object} options - Optional provider context
 * @returns {string} System prompt for LLM
 */
export const generateSystemPrompt = (templateName, sections, options = {}) => {
  if (!sections || sections.length === 0) {
    return "";
  }

  const { specialty, providerName, credentials } = options;

  // Build JSON schema from sections
  const schemaFields = sections
    .map((s) => `  "${s.key}": "${s.placeholder || s.title}"`)
    .join(",\n");

  // Build required sections note
  const requiredSections = sections.filter((s) => s.required);
  const requiredNote = requiredSections.length > 0
    ? `\n\nRequired sections that MUST be completed: ${requiredSections.map((s) => s.title).join(", ")}`
    : "";

  // Build specialty context if provided
  const specialtyContext = specialty
    ? `\n\nProvider Context: This note is being generated for a ${specialty} provider${
        credentials ? ` (${credentials})` : ""
      }. Use terminology appropriate for this specialty.`
    : "";

  return `You are a medical documentation assistant. Generate a structured ${templateName} from the patient encounter transcript.

Return ONLY valid JSON in this exact format:
{
${schemaFields}
}
${specialtyContext}

CRITICAL REQUIREMENTS:
1. ABSOLUTE PROHIBITION: You must NEVER include any fictional, fabricated, or assumed information. Only document what is explicitly stated in the transcript.
2. If information for a section is not present in the transcript, write "Not documented during this encounter" - do NOT make up details.
3. Do NOT add symptoms, findings, diagnoses, or any clinical information that was not explicitly mentioned.
4. Use professional medical terminology appropriate for clinical documentation.
5. Be concise but thorough - include all relevant details FROM THE TRANSCRIPT ONLY.
6. Format each section with clear organization.${requiredNote}`;
};

/**
 * Strip lines that are purely a label with no value, e.g. "PMH: " or "HEENT:".
 * The LLM frequently emits sub-section labels from its medical training even
 * when there is nothing to document under them. We keep lines that have
 * actual content after the colon.
 */
const stripBlankLabelLines = (text) => {
  if (!text || typeof text !== "string") return text;
  return text
    .split("\n")
    // Remove lines that are entirely a blank label, e.g. "HEENT: "
    .filter((line) => !/^[\w\s/()\-,]+:\s*$/.test(line.trim()))
    // Strip trailing blank sub-labels on lines that have real content,
    // e.g. "General: Alert and oriented. Vital Signs:" → "General: Alert and oriented."
    .map((line) => line.replace(/\s+[\w\s/()\-,]+:\s*$/, "").trimEnd())
    // Drop any lines that became empty after stripping
    .filter((line) => line.trim().length > 0)
    .join("\n")
    .trim();
};

/**
 * Coerce an arbitrary JSON value into a readable plain-text string.
 * The LLM sometimes returns nested objects/arrays for a section (e.g.
 * physicalExam as { general, vitalSigns, heent, ... }) — rendering those
 * directly as React children crashes, so flatten them to a string here.
 */
const coerceSectionValueToString = (value) => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return value
      .map((item) => coerceSectionValueToString(item))
      .filter((s) => s && s.length > 0)
      .join("\n");
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, val]) => {
        const label = key
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (c) => c.toUpperCase())
          .trim();
        const rendered = coerceSectionValueToString(val);
        return rendered ? `${label}: ${rendered}` : null;
      })
      .filter(Boolean)
      .join("\n");
  }
  return String(value);
};

/**
 * Decide whether a section's content is "effectively not documented" — i.e.
 * once you remove every "not documented" phrase, the sub-section labels, and
 * punctuation, there is no real clinical content left.
 *
 * Models routinely DISOBEY an org's "do not write 'not documented'" rule and
 * emit values like "Not documented during this encounter" or
 * "General: Not documented. Vital Signs: Not documented." anyway. For orgs that
 * asked for empty sections to be omitted, we honor that intent here rather than
 * trusting the model to return "".
 */
const isEffectivelyNotDocumented = (text) => {
  if (!text || typeof text !== "string") return false;
  const residue = text
    // Drop every "not documented" / "not documented during this encounter".
    .replace(/not documented( during this encounter)?/gi, "")
    // Drop sub-section labels like "General:", "Vital Signs:", "HEENT:".
    .replace(/[A-Za-z][A-Za-z /()\-,]*:/g, "")
    // Drop bullets, punctuation, and whitespace.
    .replace(/[\s.;,:•\-*]/g, "");
  return residue.length === 0;
};

/**
 * Parse LLM response to extract JSON note sections
 *
 * @param {string} content - Raw LLM response
 * @param {Array} sections - Expected sections from template
 * @param {Object} [options]
 * @param {boolean} [options.omitEmptySections] - When true, sections whose only
 *   content is "not documented" placeholder text are returned as "" so the UI
 *   and PDF omit them. Driven by whether the org supplied custom prompt rules.
 * @returns {Object|null} Parsed sections or null on failure
 */
export const parseNoteResponse = (content, sections, { omitEmptySections = false } = {}) => {
  if (!content) return null;

  const trimmed = content.trim();

  // Check for non-JSON responses
  if (
    trimmed.startsWith("I'm sorry") ||
    trimmed.startsWith("I cannot") ||
    trimmed.startsWith("I don't") ||
    !trimmed.includes("{")
  ) {
    console.log("[parseNoteResponse] LLM returned non-JSON response");
    const empty = {};
    sections.forEach((s) => {
      empty[s.key] = "—";
    });
    return empty;
  }

  // Extract JSON from markdown code blocks if present
  let jsonStr = content;
  const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (jsonMatch) {
    jsonStr = jsonMatch[1];
  }

  try {
    const parsed = JSON.parse(jsonStr.trim());

    // Ensure all expected sections exist and are flattened to strings
    const result = {};
    sections.forEach((s) => {
      const coerced = stripBlankLabelLines(coerceSectionValueToString(parsed[s.key])).trim();
      // Keep empty sections empty — do NOT stamp "Not documented" here.
      // For orgs that asked to omit empty sections, also blank out content the
      // model filled with "not documented" placeholder text against instructions
      // (the model disobeys the prompt, so we enforce intent here). The UI and
      // PDF then hide any blank section. Default orgs keep the text as-is.
      if (omitEmptySections && isEffectivelyNotDocumented(coerced)) {
        result[s.key] = "";
      } else {
        result[s.key] = coerced;
      }
    });

    return result;
  } catch (error) {
    console.error("[parseNoteResponse] Failed to parse JSON:", error);
    console.error("[parseNoteResponse] Content:", jsonStr.substring(0, 500));
    return null;
  }
};
